import { ref, watch, onScopeDispose, type Ref } from 'vue';
import { getMatte, type Matte, type MatteMode } from './matte';

export interface MatteState {
  /** Image URL to project — the original until extraction finishes. */
  url: string;
  /** Contour band just inside the silhouette; empty when none could be derived. */
  rimUrl: string;
  /** Which CSS mask-mode that URL wants. */
  maskMode: 'alpha' | 'luminance';
  /** Strategy the extractor settled on; surfaced as data-matte on the token. */
  mode: Exclude<MatteMode, 'auto'> | 'pending';
  ready: boolean;
  error: string | null;
}

function pending(url: string): MatteState {
  return { url, rimUrl: '', maskMode: 'luminance', mode: 'pending', ready: false, error: null };
}

/**
 * Resolve an image into a projection matte. The extractor caches per URL, so a
 * board of 40 tokens sharing a placeholder avatar runs the pixel pass exactly
 * once and every later mount resolves from cache.
 */
export function useMatte(
  src: Ref<string | undefined>,
  mode: Ref<MatteMode> | MatteMode = 'auto',
): Ref<MatteState> {
  const state = ref<MatteState>(pending(src.value || ''));
  let live = true;

  const resolveMode = () => (typeof mode === 'string' ? mode : mode.value);

  watch(
    [src, () => resolveMode()],
    ([url, matteMode]) => {
      if (!url) {
        state.value = { ...pending(''), ready: false };
        return;
      }

      state.value = pending(url);
      const requested = url;

      getMatte(url, matteMode).then(
        (m: Matte) => {
          if (!live || src.value !== requested) return;
          state.value = {
            url: m.url,
            rimUrl: m.rimUrl,
            maskMode: m.mode === 'raw' ? 'luminance' : 'alpha',
            mode: m.mode,
            ready: true,
            error: null,
          };
        },
        (err: Error) => {
          if (!live || src.value !== requested) return;
          state.value = {
            url: requested,
            rimUrl: '',
            maskMode: 'luminance',
            mode: 'raw',
            ready: true,
            error: err.message,
          };
        },
      );
    },
    { immediate: true },
  );

  onScopeDispose(() => {
    live = false;
  });

  return state;
}
